import { useEffect } from 'react';

import {
  COLLECTION_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import { toggleLeftNav } from '../../ducks/display/slice';
import { openPanel } from '../../ducks/panels/slice';
import { closePanel } from '../../ducks/panels/thunks';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import type { FCC } from '../../types/common';
import Panels from './component';
import { selectAvailablePanels, selectPanelsWithActive } from './selectors';
import type { PanelItem } from './types';

type Props = {
  className?: string;
  sidebarMenuClassName?: string;
  wrapperClassName?: string;
  panels?: PanelItem[];
  panelsHidden?: boolean;
  showSideMenu?: boolean;
};

const PanelsContainer: FCC<Props> = ({
  children,
  className,
  panels,
  panelsHidden,
  sidebarMenuClassName,
  wrapperClassName,
  showSideMenu,
}) => {
  const dispatch = useAppDispatch();
  const { route, params } = useLeafRouteParams();
  const entityId = params.id;
  const versionId = params.versionId;
  const isCollectionPage = route === COLLECTION_PAGE_ROUTE;
  const smallPanelsOpen = useAppSelector(
    (state) => state.displayReducer.leftNav,
  );
  const availablePanels = useAppSelector((state) =>
    selectAvailablePanels(state, panels, dispatch, entityId, versionId, route),
  );
  const availablePanelsWithActive = useAppSelector((state) =>
    selectPanelsWithActive(state, availablePanels),
  );
  const panelsState = useAppSelector((state) => state.panels.openedPanels);
  const isShareCollectionRoute = route === SHARE_COLLECTION_PAGE_ROUTE;

  // TODO Refactor panels to use a single source of truth. Currently, panel's active state
  //  is stored in the panels reducer and also defined in selectAvailablePanels selector as a default
  //  based on some conditions, for example turned on for comments on share pages if comments are allowed.
  //  We need to drop `active` from the PanelItem type and instead open panels on page load via `openPanel`
  //  action so we can make sure it's always in sync and uses Redux state only.
  //  Otherwise it could be the case that the panel is active by its default state from selector
  //  and we show it in the UI, but in Redux state it's not active and that leads to bugs.
  //  See https://linear.app/iconik/issue/RP-851
  // Here we open panels explicitly that are active by default to sync this into Redux state.
  useEffect(() => {
    for (const panel of availablePanels) {
      if (panel.active && panelsState[panel.key] === undefined) {
        dispatch(openPanel(panel.key));
      }
    }
  }, [availablePanels, panelsState]);

  return (
    <Panels
      className={className}
      sidebarMenuClassName={sidebarMenuClassName}
      wrapperClassName={wrapperClassName}
      panelsHidden={panelsHidden}
      openPanel={(panelKey) => dispatch(openPanel(panelKey))}
      closePanel={(panelKey) => {
        panels?.find((panel) => panel.key === panelKey)?.onClose?.(panelKey);
        dispatch(closePanel(panelKey));
      }}
      toggleSmallPanels={(value) => dispatch(toggleLeftNav(value))}
      isCollectionPage={isCollectionPage}
      smallPanelsOpen={smallPanelsOpen}
      availablePanels={availablePanelsWithActive}
      showSideMenu={showSideMenu && !isShareCollectionRoute}
    >
      {children}
    </Panels>
  );
};

export default PanelsContainer;
